import type { ChineseTip } from './main_resource.types';

export type EditorialQuestionStatus = 'draft' | 'asset_ready' | 'approved' | 'urgent' | 'archived';

export type EditorialQuestionTemplate =
  | 'T1'
  | 'T2'
  | 'T3'
  | 'T4'
  | 'T5'
  | 'T6'
  | 'T7'
  | 'T8'
  | 'T9'
  | 'T10'
  | 'T11';

/** Chinese-only templates backed by the shared word-bank editor. */
export const CHINESE_WORD_BANK_TEMPLATES: readonly EditorialQuestionTemplate[] = ['T6', 'T7', 'T8'];

export function isChineseWordBankTemplate(
  template: EditorialQuestionTemplate | string | undefined
): boolean {
  return template === 'T6' || template === 'T7' || template === 'T8';
}

export type EditorialQuestionOption = {
  id: string;
  label: string;
  content: string;
  /** Optional OSS URL for option images. Required in practice for image-option
   *  templates (T1/T2/T3/T5); optional for text-option templates that allow
   *  images (T4/T9–T11). Undefined for word-bank rows (T6–T8). */
  imageUrl?: string;
  /** Frontend-only audio URL for T6's single audio/text row. This is not part
   *  of the Henry save_all answer contract yet. */
  audioUrl?: string;
  /** Frontend-only Jyutping text for T6's single audio/text row. */
  jyutping?: string;
  /** T6 question-scoped word row used by question-only audio overrides. */
  wordId?: number;
  /** Global Chinese_word_char row selected from the word bank. */
  dictionaryWordId?: string;
  /** Global Chinese_word_char_audio row pinned to this question. */
  dictionaryAudioId?: string;
  /** True when the user uploaded whole-word audio (skip IMM recombine on save). */
  manualWordAudio?: boolean;
  /** Henry's resource_mc_answer_item.id this option maps to. Carried on the
   *  option (not inferred by position) so delete/reorder updates the correct
   *  answer row server-side and orphans only the genuinely-removed one.
   *  Undefined for options added in the editor → save_all creates a new row. */
  answerId?: number;
  /** AI-generator image prompt/requirement for image-style options. */
  imagePrompt?: string;
};

export type T6AudioWordItem = {
  audioText: string;
  jyutping: string;
  audioUrl: string;
  wordId?: string | number;
};

export type EditorialQuestionSectionNotes = {
  basicInformation: string;
  learningObjectives: string;
  templates: string;
  questionContent: string;
  options: string;
  workflow: string;
};

export type EditorialQuestion = {
  id: string;
  title: string;
  subject: string;
  level: string;
  term: string;
  topic: string;
  releaseCode: string;
  taxonomy: string;
  learningUnit: string;
  categoryName: string;
  evaluationIndicator: string;
  template: EditorialQuestionTemplate;
  learningObjective: string;
  questionContent: string;
  difficulty: string;
  options: EditorialQuestionOption[];
  correctOptionId: string;
  explanation: string;
  generatedDraftQuestionId?: string;
  generatedTemplateId?: EditorialQuestionTemplate;
  generatedOptionType?: string;
  generatedOptionLayout?: string;
  generatedExplanation?: string;
  imageHint: string;
  audioHint: string;
  /** Background image OSS URL (Henry get_full_info background[]). */
  backgroundHint?: string;
  /** Question video OSS URL (Henry get_full_info video[]). */
  videoHint?: string;
  assetReady: boolean;
  stored: boolean;
  workflowNotes: string;
  sectionNotes: EditorialQuestionSectionNotes;
  status: EditorialQuestionStatus;
  updatedAt: string;
  // Henry-side binding fields. Populated by editorialQuestionHenryClient
  // when working against the real backend; left undefined by the mock.
  // Required for save_all (res_id), oss_signurl_* (resource_item_id /
  // answer_ids), and the editorial taxonomy cascade (topic_id).
  res_id?: number;
  resource_item_id?: number;
  adapter_id?: number | null;
  answer_ids?: number[];
  topic_id?: number;
  /** From save_all for T6/mc_chinese — resource_chinese_word_audio.id. */
  chinese_word_audio_id?: number | null;
  /** Soft-fail reason when save_all could not combine word audio. */
  chinese_word_audio_combine_reason?: string | null;
  /** T7/T8 recording tip audio as loaded from get_full_info. */
  chineseTip?: ChineseTip;
};

export type EditorialQuestionInput = Omit<EditorialQuestion, 'id' | 'updatedAt'>;

export type EditorialQuestionListParams = {
  page?: number;
  limit?: number;
  keyword?: string;
  subject?: string;
  scope?: string;
  unit?: string;
  /** Numeric topic_type_id for the 4th-level taxonomy leaf. The page
   *  resolves the user-picked (subject, scope, unit, topic) cascade to
   *  this id via findTopicTypeId before calling listPaged. */
  topic_id?: number;
  template?: EditorialQuestionTemplate;
  status?: EditorialQuestionStatus;
};

export type EditorialQuestionListResult = {
  rows: EditorialQuestion[];
  total: number;
};

// Mock client base URL — sourced strictly from env, no hardcoded fallback.
// `yarn mock:api` runs json-server on a chosen port; set
// REACT_APP_EDITORIAL_QUESTION_MOCK_API_URL in .env.local (e.g.
// http://127.0.0.1:3032) to opt into the mock. Missing var = empty base
// URL, which will surface as visible "Failed to fetch" errors instead of
// silently hitting a local json-server that may or may not be running.
const API_BASE_URL = (process.env.REACT_APP_EDITORIAL_QUESTION_MOCK_API_URL || '').replace(
  /\/$/,
  ''
);
const EDITORIAL_QUESTIONS_URL = `${API_BASE_URL}/editorialQuestions`;
const VALID_STATUSES: EditorialQuestionStatus[] = [
  'draft',
  'asset_ready',
  'approved',
  'urgent',
  'archived'
];
const VALID_TEMPLATES: EditorialQuestionTemplate[] = [
  'T1',
  'T2',
  'T3',
  'T4',
  'T5',
  'T6',
  'T7',
  'T8',
  'T9',
  'T10',
  'T11'
];
const OPTION_KEYS = ['A', 'B', 'C', 'D'];

type QuestionRecord = Partial<EditorialQuestion> & Record<string, unknown>;

function toText(value: unknown, fallback = '') {
  if (value === null || value === undefined) {
    return fallback;
  }

  const text = String(value).trim();
  return text || fallback;
}

function uniqueTexts(values: string[], limit: number) {
  const seen = new Set<string>();
  const output: string[] = [];

  values.forEach((value) => {
    const text = toText(value);
    if (!text || seen.has(text)) {
      return;
    }
    seen.add(text);
    output.push(text);
  });

  return output.slice(0, limit);
}

function uniqueT6AudioWordItems(values: T6AudioWordItem[], limit: number) {
  const seen = new Set<string>();
  const output: T6AudioWordItem[] = [];

  values.forEach((value) => {
    const audioText = toText(value.audioText);
    if (!audioText) {
      return;
    }
    const key = [
      audioText,
      toText(value.jyutping),
      toText(value.audioUrl),
      toText(value.wordId)
    ].join('\u0000');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    output.push({ ...value, audioText });
  });

  return output.slice(0, limit);
}

function toBoolean(value: unknown, fallback = false) {
  if (typeof value === 'boolean') {
    return value;
  }

  const text = toText(value).toLowerCase();
  if (['true', '1', 'yes', 'y', '是'].includes(text)) {
    return true;
  }

  if (['false', '0', 'no', 'n', '否'].includes(text)) {
    return false;
  }

  return fallback;
}

function toOptionalNumber(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

function toStatus(value: unknown): EditorialQuestionStatus {
  const status = toText(value) as EditorialQuestionStatus;
  return VALID_STATUSES.includes(status) ? status : 'draft';
}

function toTemplate(value: unknown): EditorialQuestionTemplate {
  const template = toText(value) as EditorialQuestionTemplate;
  return VALID_TEMPLATES.includes(template) ? template : 'T1';
}

function toUpdatedAt(value: unknown) {
  const text = toText(value);
  const date = text ? new Date(text) : null;

  if (date && !Number.isNaN(date.getTime())) {
    return date.toISOString();
  }

  return new Date().toISOString();
}

function normalizeOptions(value: unknown): EditorialQuestionOption[] {
  const rawOptions = Array.isArray(value) ? value : [];
  const optionKeys = rawOptions.length
    ? OPTION_KEYS.slice(0, Math.min(rawOptions.length, OPTION_KEYS.length))
    : OPTION_KEYS;

  return optionKeys.map((key) => {
    const id = key.toLowerCase();
    const option = rawOptions.find((item) => {
      const rawOption = item as Partial<EditorialQuestionOption>;
      return (
        toText(rawOption.id).toLowerCase() === id ||
        toText(rawOption.label).toLowerCase() === key.toLowerCase()
      );
    }) as Partial<EditorialQuestionOption> | undefined;

    const rawOption = (option || {}) as Partial<EditorialQuestionOption> & { text?: unknown };
    const imageUrl = rawOption.imageUrl ? toText(rawOption.imageUrl) : '';
    const audioUrl = rawOption.audioUrl ? toText(rawOption.audioUrl) : '';
    const jyutping = rawOption.jyutping ? toText(rawOption.jyutping) : '';
    const imagePrompt = rawOption.imagePrompt ? toText(rawOption.imagePrompt) : '';
    const content = toText(rawOption.content ?? rawOption.text);
    const isPlaceholder = [key, `Answer ${key}`, `選項 ${key}`].includes(content);
    return {
      id,
      label: toText(rawOption.label, key),
      content: isPlaceholder ? '' : content,
      ...(imageUrl ? { imageUrl } : {}),
      ...(audioUrl ? { audioUrl } : {}),
      ...(jyutping ? { jyutping } : {}),
      ...(imagePrompt ? { imagePrompt } : {})
    };
  });
}

function normalizeCorrectOptionId(value: unknown, options: EditorialQuestionOption[]) {
  const correctOption = toText(value, options[0]?.id || 'a');
  const normalized = correctOption.toLowerCase();
  const match = options.find(
    (option) =>
      option.id === normalized ||
      option.label.toLowerCase() === normalized ||
      option.content === correctOption
  );

  return match?.id || options[0]?.id || 'a';
}

function normalizeSectionNotes(value: unknown): EditorialQuestionSectionNotes {
  const notes = (value || {}) as Partial<Record<keyof EditorialQuestionSectionNotes, unknown>>;

  return {
    basicInformation: toText(notes.basicInformation),
    learningObjectives: toText(notes.learningObjectives),
    templates: toText(notes.templates),
    questionContent: toText(notes.questionContent),
    options: toText(notes.options),
    workflow: toText(notes.workflow)
  };
}

function normalizeQuestion(record: QuestionRecord, index = 0): EditorialQuestion {
  const template = toTemplate(record.template);
  const options = normalizeOptions(record.options).slice(
    0,
    template === 'T7' || template === 'T8' ? 1 : OPTION_KEYS.length
  );
  const learningObjective = toText(record.learningObjective, toText(record.evaluationIndicator));
  const taxonomy = toText(record.taxonomy, '數與量');

  return {
    id: toText(record.id, `mock-${index + 1}`),
    title: toText(record.title, `Mock 題目 ${index + 1}`),
    subject: toText(record.subject, 'math'),
    level: toText(record.level, 'K2'),
    term: toText(record.term, '上學期'),
    topic: toText(record.topic, toText(record.learningUnit, taxonomy)),
    releaseCode: toText(record.releaseCode),
    taxonomy,
    learningUnit: toText(record.learningUnit),
    categoryName: toText(record.categoryName),
    evaluationIndicator: toText(record.evaluationIndicator, learningObjective),
    template,
    learningObjective,
    questionContent: toText(record.questionContent, '請輸入題目內容。'),
    difficulty: toText(record.difficulty, '1'),
    options,
    correctOptionId: normalizeCorrectOptionId(record.correctOptionId, options),
    explanation: toText(record.explanation),
    generatedDraftQuestionId: toText(record.generatedDraftQuestionId),
    generatedTemplateId: record.generatedTemplateId
      ? toTemplate(record.generatedTemplateId)
      : undefined,
    generatedOptionType: toText(record.generatedOptionType),
    generatedOptionLayout: toText(record.generatedOptionLayout),
    generatedExplanation: toText(record.generatedExplanation),
    imageHint: toText(record.imageHint),
    audioHint: toText(record.audioHint),
    backgroundHint: toText(record.backgroundHint),
    videoHint: toText(record.videoHint),
    assetReady: toBoolean(record.assetReady),
    stored: toBoolean(record.stored),
    workflowNotes: toText(record.workflowNotes),
    sectionNotes: normalizeSectionNotes(record.sectionNotes),
    status: toStatus(record.status),
    updatedAt: toUpdatedAt(record.updatedAt),
    res_id: toOptionalNumber(record.res_id),
    resource_item_id: toOptionalNumber(record.resource_item_id),
    adapter_id: toOptionalNumber(record.adapter_id) ?? null,
    answer_ids: Array.isArray(record.answer_ids)
      ? record.answer_ids.map(toOptionalNumber).filter((value): value is number => value != null)
      : undefined,
    topic_id: toOptionalNumber(record.topic_id)
  };
}

function makeId() {
  return `mock-${Date.now()}`;
}

function matchesText(value: string, keyword: string) {
  return value.toLowerCase().includes(keyword.toLowerCase());
}

function filterQuestions(
  questions: EditorialQuestion[],
  params: EditorialQuestionListParams
): EditorialQuestion[] {
  const keyword = toText(params.keyword);

  return questions.filter((question) => {
    const matchKeyword =
      !keyword ||
      [
        question.id,
        question.releaseCode,
        question.title,
        question.categoryName,
        question.questionContent,
        question.taxonomy,
        ...Object.values(question.sectionNotes)
      ].some((value) => matchesText(value, keyword));
    const matchSubject = !params.subject || question.subject === params.subject;
    const matchScope = !params.scope || question.taxonomy === params.scope;
    const matchUnit = !params.unit || question.learningUnit === params.unit;
    // topic_id is the numeric topic_type_id. Mock question rows carry the
    // resolved id as `topicId` (optional). If the row doesn't have one, the
    // filter can't match — acceptable for local mock data, since the Henry
    // client is the production path.
    const matchTopic =
      !params.topic_id || (question as { topicId?: number }).topicId === params.topic_id;
    const matchTemplate = !params.template || question.template === params.template;
    const matchStatus = !params.status || question.status === params.status;

    return (
      matchKeyword &&
      matchSubject &&
      matchScope &&
      matchUnit &&
      matchTopic &&
      matchTemplate &&
      matchStatus
    );
  });
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers
    }
  });

  if (!response.ok) {
    throw new Error(`Editorial question mock API request failed: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json();
}

export const editorialQuestionClient = {
  async list() {
    const questions = await request<QuestionRecord[]>(EDITORIAL_QUESTIONS_URL);
    return questions.map(normalizeQuestion);
  },

  async listPaged(params: EditorialQuestionListParams = {}): Promise<EditorialQuestionListResult> {
    const page = params.page ?? 0;
    const limit = params.limit ?? 10;
    const questions = await request<QuestionRecord[]>(EDITORIAL_QUESTIONS_URL);
    const filtered = filterQuestions(questions.map(normalizeQuestion), params);
    const offset = page * limit;

    return {
      rows: filtered.slice(offset, offset + limit),
      total: filtered.length
    };
  },

  async listT6AudioWordItems(limit = 20): Promise<T6AudioWordItem[]> {
    const questions = await request<QuestionRecord[]>(EDITORIAL_QUESTIONS_URL);
    return uniqueT6AudioWordItems(
      questions
        .map(normalizeQuestion)
        .filter((question) => isChineseWordBankTemplate(question.template))
        .map((question) => {
          const option = question.options[0];
          return {
            audioText: option?.content || '',
            jyutping: option?.jyutping || '',
            audioUrl: option?.audioUrl || '',
            wordId: question.resource_item_id
          };
        }),
      limit
    );
  },

  async listT6AudioTextSuggestions(limit = 20): Promise<string[]> {
    const items = await this.listT6AudioWordItems(limit);
    return uniqueTexts(
      items.map((item) => item.audioText),
      limit
    );
  },

  /** Fetch a single question by id (panel id = adapter_id on Henry path; for
   *  the mock it's the json-server primary key). Used by direct-URL access
   *  `/questions/:adapterId`. */
  async get(id: string) {
    const question = await request<QuestionRecord>(`${EDITORIAL_QUESTIONS_URL}/${id}`);
    return normalizeQuestion(question);
  },

  /** Create a blank draft on the server so subsequent file uploads have a
   *  question id (material_id) to attach to. Lets the editor switch from
   *  "save uploads everything" to "save just confirms uploads finished". */
  async createDraft(template: EditorialQuestionTemplate = 'T1') {
    const updatedAt = new Date().toISOString();
    const id = makeId();
    const shell = normalizeQuestion({ id, status: 'draft', updatedAt, template });
    const created = await request<QuestionRecord>(EDITORIAL_QUESTIONS_URL, {
      method: 'POST',
      body: JSON.stringify(shell)
    });
    return normalizeQuestion(created);
  },

  async save(input: EditorialQuestionInput, id?: string) {
    const updatedAt = new Date().toISOString();

    if (id) {
      const updated = normalizeQuestion({ ...input, id, updatedAt });
      const question = await request<QuestionRecord>(`${EDITORIAL_QUESTIONS_URL}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updated)
      });
      return normalizeQuestion(question);
    }

    const created = normalizeQuestion({
      ...input,
      id: makeId(),
      updatedAt
    });
    const question = await request<QuestionRecord>(EDITORIAL_QUESTIONS_URL, {
      method: 'POST',
      body: JSON.stringify(created)
    });
    return normalizeQuestion(question);
  },

  async transition(id: string, status: EditorialQuestionStatus) {
    const question = await request<QuestionRecord>(`${EDITORIAL_QUESTIONS_URL}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: toStatus(status),
        updatedAt: new Date().toISOString()
      })
    });
    return normalizeQuestion(question);
  },

  async remove(id: string) {
    await request<void>(`${EDITORIAL_QUESTIONS_URL}/${id}`, {
      method: 'DELETE'
    });
  }
};

export const editorialQuestionMockClient = editorialQuestionClient;
